import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BadgeCheck, MapPin, Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ArrowUpRight, Edit3, Trash2, X, Loader2 } from "lucide-react";

/**
 * Instagram / Facebook style vertical feed:
 *  - narrow column (max-w ~470px), stacked cards
 *  - image sits in the card (aspect-square by default, taller for portrait)
 *  - action bar: like · comment (opens author DM) · share · save
 *  - like/save persist to backend
 */
export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts/feed")
      .then(r => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-[#8A8A8A]" data-testid="feed-loading">Loading the work of good Hoomanz…</div>;

  return (
    <div className="max-w-[520px] mx-auto px-3 sm:px-0 py-6 space-y-6" data-testid="feed-container">
      {posts.length === 0 && (
        <div className="card p-12 text-center text-[#8A8A8A]" data-testid="feed-empty">
          No posts yet — be the first to share.
        </div>
      )}
      {posts.map((p) => (
        <PostCard key={p.id} post={p} currentUser={user}/>
      ))}
    </div>
  );
}

function PostCard({ post: initialPost, currentUser }) {
  const [post, setPost] = useState(initialPost);
  const [liked, setLiked] = useState(!!initialPost.liked_by_me);
  const [likeCount, setLikeCount] = useState(Number(initialPost.like_count || 0));
  const [saved, setSaved] = useState(!!initialPost.saved_by_me);
  const [busyLike, setBusyLike] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(Number(initialPost.comment_count || 0));
  const menuRef = useRef(null);

  const author = post.author || {};
  const timeAgo = useTimeAgo(post.created_at);
  const isOwn = currentUser && author.id === currentUser.id;

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const remove = async () => {
    if (!confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      setRemoved(true);
    } catch { toast.error("Couldn't delete post."); }
  };

  const toggleLike = async () => {
    if (!currentUser) { toast.error("Sign in to react to posts."); return; }
    if (busyLike) return;
    // optimistic
    const next = !liked;
    setLiked(next);
    setLikeCount(c => c + (next ? 1 : -1));
    setBusyLike(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(!!data.liked);
      setLikeCount(Number(data.like_count || 0));
    } catch {
      // rollback
      setLiked(!next);
      setLikeCount(c => c + (next ? -1 : 1));
      toast.error("Couldn't update your reaction.");
    } finally { setBusyLike(false); }
  };

  const toggleSave = async () => {
    if (!currentUser) { toast.error("Sign in to save posts."); return; }
    if (busySave) return;
    const next = !saved;
    setSaved(next);
    setBusySave(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/save`);
      setSaved(!!data.saved);
      if (data.saved) toast.success("Saved to your collection");
    } catch {
      setSaved(!next);
      toast.error("Couldn't save this post.");
    } finally { setBusySave(false); }
  };

  const share = async () => {
    const url = `${window.location.origin}/u/${author.id}`;
    const shareData = {
      title: `${author.name || "A Hooman"} on Hoomanz`,
      text: post.caption || "Check out this human on Hoomanz.",
      url,
    };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch { /* user cancelled share sheet */ }
  };

  return (
    removed ? null :
    <article className="bg-white rounded-2xl border border-[#E5E5E3] overflow-hidden"
             data-testid={`feed-post-${post.id}`}>
      {editing && (
        <EditCaptionModal
          post={post}
          onClose={()=>setEditing(false)}
          onSaved={(updated)=>{ setPost(p => ({...p, caption: updated.caption})); setEditing(false); toast.success("Post updated"); }}
        />
      )}
      <div className="flex items-center gap-3 px-4 py-3">
        <Link to={`/u/${author.id}`} className="shrink-0" data-testid={`feed-author-${post.id}`}>
          {author.avatar
            ? <img src={author.avatar} alt="" className="h-9 w-9 rounded-full object-cover bg-[#E5E5E3]"/>
            : <div className="h-9 w-9 rounded-full bg-[#E5E5E3]"/>}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <Link to={`/u/${author.id}`} className="text-sm font-medium hover:underline truncate">
              {author.name || "Unknown"}
            </Link>
            {author.verified && <BadgeCheck size={13} className="text-[#0057FF] shrink-0"/>}
            {post.is_sponsored && (
              <span className="ml-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F2F2F0] text-[#4A4A4A]" data-testid={`feed-sponsored-${post.id}`}>
                Sponsored
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-[#8A8A8A]">
            {author.category && <span className="truncate">{author.category}</span>}
            {author.location && (
              <>
                <span>·</span>
                <MapPin size={10}/> <span className="truncate">{author.location}</span>
              </>
            )}
          </div>
        </div>
        <Link to={`/u/${author.id}`}
              className="hidden sm:inline-flex btn-ghost !py-1.5 !px-3 text-xs"
              data-testid={`feed-book-${post.id}`}>
          Book <ArrowUpRight size={12}/>
        </Link>
        <div className="relative" ref={menuRef}>
          <button onClick={()=>setMenuOpen(o=>!o)}
                  className="h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center text-[#4A4A4A]"
                  aria-label="More options" data-testid={`feed-more-${post.id}`}>
            <MoreHorizontal size={16}/>
          </button>
          {menuOpen && isOwn && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-lg border border-[#E5E5E3] overflow-hidden z-20"
                 data-testid={`feed-menu-${post.id}`}>
              <button onClick={()=>{ setEditing(true); setMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#F2F2F0]"
                      data-testid={`feed-edit-${post.id}`}>
                <Edit3 size={13}/> Edit caption
              </button>
              <button onClick={()=>{ setMenuOpen(false); remove(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-[#B84B4B] hover:bg-[#F2F2F0]"
                      data-testid={`feed-delete-${post.id}`}>
                <Trash2 size={13}/> Delete post
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Media */}
      {post.image && (
        <div className="px-3 pb-3">
          <div className="rounded-3xl overflow-hidden bg-[#F2F2F0]">
            <img src={post.image} alt=""
                 data-lightbox
                 data-lightbox-group={`feed-${post.id}`}
                 className="w-full max-h-[600px] object-cover cursor-zoom-in"/>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-1 px-2 py-2">
        <button onClick={toggleLike} disabled={busyLike}
                className={`h-10 w-10 rounded-full inline-flex items-center justify-center transition-colors
                  ${liked ? "text-[#E11D48] hover:bg-[#FEECEE]" : "text-[#0A0A0A] hover:bg-[#F2F2F0]"}`}
                aria-label={liked ? "Unlike" : "Like"}
                data-testid={`feed-like-${post.id}`}>
          <Heart size={22} fill={liked ? "currentColor" : "none"} strokeWidth={liked ? 0 : 2}/>
        </button>
        <Link to={`/messages/${author.id}`}
              className="hidden h-10 w-10 rounded-full items-center justify-center text-[#0A0A0A] hover:bg-[#F2F2F0]"
              aria-label="Message" data-testid={`feed-message-${post.id}`}>
          <Send size={20}/>
        </Link>
        <button onClick={()=>setCommentsOpen(o=>!o)}
                className={`h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-[#F2F2F0] ${commentsOpen ? "text-[#0057FF]" : "text-[#0A0A0A]"}`}
                aria-label="Comments" data-testid={`feed-comment-${post.id}`}>
          <MessageCircle size={22}/>
        </button>
        <button onClick={share}
                className="h-10 w-10 rounded-full inline-flex items-center justify-center text-[#0A0A0A] hover:bg-[#F2F2F0]"
                aria-label="Share" data-testid={`feed-share-${post.id}`}>
          <Send size={20}/>
        </button>
        <button onClick={toggleSave} disabled={busySave}
                className={`h-10 w-10 rounded-full ml-auto inline-flex items-center justify-center transition-colors
                  ${saved ? "text-[#0057FF] hover:bg-[#EEF3FF]" : "text-[#0A0A0A] hover:bg-[#F2F2F0]"}`}
                aria-label={saved ? "Unsave" : "Save"}
                data-testid={`feed-save-${post.id}`}>
          <Bookmark size={22} fill={saved ? "currentColor" : "none"} strokeWidth={saved ? 0 : 2}/>
        </button>
      </div>

      {/* Meta + caption */}
      <div className="px-4 pb-4 pt-1">
        <p className="text-sm font-medium" data-testid={`feed-likes-${post.id}`}>
          {likeCount === 0 ? "Be the first to like this"
            : likeCount === 1 ? "1 like"
            : `${likeCount.toLocaleString()} likes`}
        </p>
        {post.caption && (
          <p className="text-sm mt-1.5 whitespace-pre-wrap" data-testid={`feed-caption-${post.id}`}>
            <Link to={`/u/${author.id}`} className="font-medium hover:underline">{author.name}</Link>{" "}
            <span>{post.caption}</span>
          </p>
        )}
        {commentCount > 0 && !commentsOpen && (
          <button onClick={()=>setCommentsOpen(true)}
                  className="text-xs text-[#8A8A8A] mt-2 hover:underline"
                  data-testid={`feed-view-comments-${post.id}`}>
            View {commentCount === 1 ? "1 comment" : `all ${commentCount.toLocaleString()} comments`}
          </button>
        )}
        <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] mt-2" data-testid={`feed-time-${post.id}`}>
          {timeAgo}
        </p>
      </div>

      {commentsOpen && (
        <CommentsSection
          postId={post.id}
          postAuthorId={author.id}
          currentUser={currentUser}
          onCountChange={setCommentCount}
        />
      )}
    </article>
  );
}

/** Very small "5 min ago" formatter — avoids pulling in a date library. */
function useTimeAgo(iso) {
  const [label, setLabel] = useState(() => fmt(iso));
  useEffect(() => {
    setLabel(fmt(iso));
    const t = setInterval(() => setLabel(fmt(iso)), 60_000);
    return () => clearInterval(t);
  }, [iso]);
  return label;
}
function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff/60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)} h ago`;
  if (diff < 604800) return `${Math.floor(diff/86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function EditCaptionModal({ post, onClose, onSaved }) {
  const [caption, setCaption] = useState(post.caption || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { caption });
      onSaved(data);
    } catch { toast.error("Couldn't save changes."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid={`feed-edit-modal-${post.id}`}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading text-lg">Edit caption</p>
          <button onClick={onClose} aria-label="Close" className="text-[#4A4A4A]"><X size={16}/></button>
        </div>
        <textarea
          rows={5}
          value={caption}
          onChange={(e)=>setCaption(e.target.value)}
          maxLength={2000}
          className="input resize-none"
          placeholder="What did you want to say?"
          data-testid={`feed-edit-input-${post.id}`}
        />
        <div className="flex gap-2 mt-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid={`feed-edit-save-${post.id}`}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}



function CommentsSection({ postId, postAuthorId, currentUser, onCountChange }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/posts/${postId}/comments`);
      setItems(data || []);
      onCountChange?.((data || []).length);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [postId]);

  const submit = async (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v) return;
    if (!currentUser) { toast.error("Sign in to comment."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/comments`, { text: v });
      setItems(prev => [...(prev || []), data]);
      onCountChange?.((items?.length || 0) + 1);
      setText("");
    } catch { toast.error("Couldn't post your comment."); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await api.delete(`/posts/${postId}/comments/${c.id}`);
      const next = (items || []).filter(x => x.id !== c.id);
      setItems(next);
      onCountChange?.(next.length);
    } catch { toast.error("Couldn't delete."); }
  };

  const toggleLike = async (c) => {
    if (!currentUser) { toast.error("Sign in to react."); return; }
    // optimistic
    const wasLiked = c.liked_by_me;
    setItems(prev => (prev || []).map(x => x.id === c.id
      ? { ...x, liked_by_me: !wasLiked, like_count: Math.max(0, (x.like_count || 0) + (wasLiked ? -1 : 1)) }
      : x));
    try {
      const { data } = await api.post(`/posts/${postId}/comments/${c.id}/like`);
      setItems(prev => (prev || []).map(x => x.id === c.id
        ? { ...x, liked_by_me: !!data.liked, like_count: Number(data.like_count || 0) }
        : x));
    } catch {
      // rollback
      setItems(prev => (prev || []).map(x => x.id === c.id
        ? { ...x, liked_by_me: wasLiked, like_count: Math.max(0, (x.like_count || 0) + (wasLiked ? 1 : -1)) }
        : x));
    }
  };

  return (
    <div className="border-t border-[#E5E5E3] px-4 py-4 space-y-3" data-testid={`feed-comments-${postId}`}>
      {items === null && <p className="text-xs text-[#8A8A8A]" data-testid={`feed-comments-loading-${postId}`}>Loading comments…</p>}
      {items && items.length === 0 && <p className="text-xs text-[#8A8A8A]" data-testid={`feed-comments-empty-${postId}`}>No comments yet — say hi.</p>}
      {(items || []).map(c => (
        <div key={c.id} className="flex items-start gap-2" data-testid={`feed-comment-${c.id}`}>
          <Link to={`/u/${c.author?.id}`} className="shrink-0">
            {c.author?.avatar
              ? <img src={c.author.avatar} alt="" className="h-7 w-7 rounded-full object-cover bg-[#E5E5E3]"/>
              : <div className="h-7 w-7 rounded-full bg-[#E5E5E3]"/>}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-sm">
              <Link to={`/u/${c.author?.id}`} className="font-medium hover:underline">{c.author?.name || "Someone"}</Link>
              <span className="ml-1.5 whitespace-pre-wrap">{c.text}</span>
            </div>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-[#8A8A8A]">
              <span>{fmt(c.created_at)}</span>
              <button onClick={()=>toggleLike(c)}
                      className={`inline-flex items-center gap-1 hover:text-[#0A0A0A] ${c.liked_by_me ? "text-[#E11D48]" : ""}`}
                      data-testid={`feed-comment-like-${c.id}`}>
                <Heart size={11} fill={c.liked_by_me ? "currentColor" : "none"}/>
                {c.like_count > 0 ? c.like_count : "Like"}
              </button>
              {c.can_delete && (
                <button onClick={()=>remove(c)} className="text-[#8A8A8A] hover:text-[#B84B4B]" data-testid={`feed-comment-delete-${c.id}`}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {currentUser ? (
        <form onSubmit={submit} className="flex items-center gap-2 pt-2" data-testid={`feed-comment-form-${postId}`}>
          <input
            value={text}
            onChange={(e)=>setText(e.target.value)}
            placeholder="Add a comment…"
            maxLength={1000}
            className="input py-2 !text-sm"
            data-testid={`feed-comment-input-${postId}`}
          />
          <button type="submit" disabled={busy || !text.trim()}
                  className="btn-primary !py-2 !px-3 text-sm shrink-0"
                  data-testid={`feed-comment-submit-${postId}`}>
            {busy ? <Loader2 size={14} className="animate-spin"/> : "Post"}
          </button>
        </form>
      ) : (
        <p className="text-xs text-[#8A8A8A] pt-2">
          <Link to="/login" className="underline">Sign in</Link> to comment.
        </p>
      )}
    </div>
  );
}
